import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    IconButton
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    EditOutlined as EditIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { toPersianDigits } from '../../../../utils/convertNumbers';

// Persian day names, index = backend day value (0 = شنبه)
const DAY_CHOICES = [
    { value: 0, label: 'شنبه' },
    { value: 1, label: 'یکشنبه' },
    { value: 2, label: 'دوشنبه' },
    { value: 3, label: 'سه‌شنبه' },
    { value: 4, label: 'چهارشنبه' },
    { value: 5, label: 'پنج‌شنبه' },
    { value: 6, label: 'جمعه' }
];

const SLOT_MINUTES = 15; // grid row granularity
const SLOT_HEIGHT = 14;  // px per 15-minute row

const toMinutes = (timeString) => {
    if (!timeString) return null;
    const [h, m] = timeString.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
};

const formatTime = (minutes) => {
    const h = String(Math.floor(minutes / 60)).padStart(2, '0');
    const m = String(minutes % 60).padStart(2, '0');
    return toPersianDigits(`${h}:${m}`);
};

// JS getDay(): Sunday=0 … Saturday=6 → backend day: شنبه=0 … جمعه=6
const todayBackendDay = () => (new Date().getDay() + 1) % 7;

export default function TeacherScheduleTimeGrid({ teachers, instituteSettings, onGroupClick, onEditTeacher }) {
    const [selectedDay, setSelectedDay] = useState(todayBackendDay);

    // Drag-to-pan: pan the scroll container with the mouse; a real drag
    // must also suppress the click on group blocks it passes over
    const scrollRef = useRef(null);
    const dragRef = useRef({ active: false, moved: false, x: 0, y: 0, left: 0, top: 0 });
    const [grabbing, setGrabbing] = useState(false);

    const handlePointerDown = (e) => {
        if (e.button !== 0 || e.pointerType !== 'mouse' || !scrollRef.current) return;
        const el = scrollRef.current;
        dragRef.current = {
            active: true, moved: false,
            x: e.clientX, y: e.clientY,
            left: el.scrollLeft, top: el.scrollTop
        };
        // Do NOT capture the pointer here: capture retargets the eventual
        // click to the container, breaking clicks on blocks/buttons inside.
    };

    const handlePointerMove = (e) => {
        const drag = dragRef.current;
        if (!drag.active) return;
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 6) {
            drag.moved = true;
            setGrabbing(true);
            // A real drag started: now capturing is safe (and keeps the pan
            // tracking even when the pointer leaves the container)
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        if (drag.moved) {
            scrollRef.current.scrollLeft = drag.left - dx;
            scrollRef.current.scrollTop = drag.top - dy;
        }
    };

    const handlePointerUp = () => {
        dragRef.current.active = false;
        setGrabbing(false);
        // keep `moved` until after the click event this pointer-up spawns
        setTimeout(() => { dragRef.current.moved = false; }, 0);
    };

    const pageScroll = (direction) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
    };

    // Edge paging arrows only make sense when the grid actually overflows
    const [canScrollX, setCanScrollX] = useState(false);

    const defaultStart = instituteSettings?.work_hours_start || '08:00';
    const defaultEnd = instituteSettings?.work_hours_end || '20:00';

    const { gridStart, gridEnd, columns } = useMemo(() => {
        let minM = Infinity;
        let maxM = -Infinity;

        const cols = (teachers || []).map((entry) => {
            const workingDay = (entry.working_days || {})[selectedDay];
            const sessions = ((entry.scheduled_classes || {})[selectedDay] || [])
                .map((slot) => ({
                    ...slot,
                    startMinutes: toMinutes(slot.start_time),
                    endMinutes: toMinutes(slot.end_time)
                }))
                .filter((slot) => slot.startMinutes !== null && slot.endMinutes > slot.startMinutes)
                .sort((a, b) => a.startMinutes - b.startMinutes);

            const isWorking = Boolean(workingDay?.is_working) || sessions.length > 0;
            const workStart = toMinutes(
                (workingDay?.is_working && workingDay.start_time) ? workingDay.start_time : defaultStart
            );
            const workEnd = toMinutes(
                (workingDay?.is_working && workingDay.end_time) ? workingDay.end_time : defaultEnd
            );

            if (isWorking) {
                minM = Math.min(minM, workStart, ...sessions.map((s) => s.startMinutes));
                maxM = Math.max(maxM, workEnd, ...sessions.map((s) => s.endMinutes));
            }

            return { teacher: entry.teacher, isWorking, workStart, workEnd, sessions };
        });

        // Off-day teachers are noise in the all-teachers view, but a single
        // explicitly-filtered teacher stays visible on every day so their
        // schedule can be inspected (and first set up) via the edit drawer
        const visibleCols = cols.length === 1 ? cols : cols.filter((c) => c.isWorking);

        if (!Number.isFinite(minM)) {
            minM = toMinutes(defaultStart);
            maxM = toMinutes(defaultEnd);
        }
        // Snap the axis to full hours
        minM = Math.floor(minM / 60) * 60;
        maxM = Math.ceil(maxM / 60) * 60;

        return { gridStart: minM, gridEnd: maxM, columns: visibleCols };
    }, [teachers, selectedDay, defaultStart, defaultEnd]);

    useEffect(() => {
        const el = scrollRef.current;
        setCanScrollX(Boolean(el && el.scrollWidth > el.clientWidth + 4));
    }, [columns]);

    const totalSlots = Math.max(1, (gridEnd - gridStart) / SLOT_MINUTES);
    // Row 1 is the header; slot rows start at 2
    const rowOf = (minutes) =>
        Math.min(totalSlots + 2, Math.max(2, Math.round((minutes - gridStart) / SLOT_MINUTES) + 2));

    const hourMarks = [];
    for (let m = gridStart; m <= gridEnd; m += 60) hourMarks.push(m);

    const renderSessionBlock = (column, gridColumn, slot, index) => {
        const groupClass = slot.classes?.find((c) => c.course.course_type === 'group');
        const courseNames = (slot.classes || []).map((c) => c.course.course_name).join('، ');
        const detailText = (slot.classes || [])
            .map((c) => {
                const students = (c.students || []).map((s) => s.full_name).join('، ');
                return `${c.course.course_name}: ${students || 'بدون هنرجو'}`;
            })
            .join(' — ');
        const subtitle = groupClass
            ? `${toPersianDigits(groupClass.students?.length || 0)} هنرجو`
            : slot.classes?.[0]?.students?.[0]?.full_name || 'بدون هنرجو';

        return (
            <Tooltip
                key={`session-${column.teacher.id}-${index}`}
                title={`${formatTime(slot.startMinutes)} - ${formatTime(slot.endMinutes)} | ${detailText}`}
                placement="top"
                arrow
            >
                <Box
                    onClick={groupClass
                        ? () => { if (!dragRef.current.moved) onGroupClick(groupClass); }
                        : undefined}
                    sx={(theme) => ({
                        gridColumn,
                        gridRow: `${rowOf(slot.startMinutes)} / ${rowOf(slot.endMinutes)}`,
                        zIndex: 2,
                        mx: 0.5,
                        px: 0.75,
                        py: 0.25,
                        overflow: 'hidden',
                        borderRadius: 1,
                        cursor: groupClass ? 'pointer' : 'default',
                        bgcolor: alpha(
                            groupClass ? theme.palette.secondary.main : theme.palette.primary.main,
                            0.18
                        ),
                        border: '1px solid',
                        borderColor: groupClass ? 'secondary.main' : 'primary.main',
                        '&:hover': groupClass ? { boxShadow: 2 } : {}
                    })}
                >
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                        {`${formatTime(slot.startMinutes)} ${courseNames}`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                        {subtitle}
                    </Typography>
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box>
            {/* Toolbar: day selector */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <ToggleButtonGroup
                    value={selectedDay}
                    exclusive
                    onChange={(event, value) => value !== null && setSelectedDay(value)}
                    size="small"
                    sx={{ flexWrap: 'wrap' }}
                >
                    {DAY_CHOICES.map(({ value, label }) => (
                        <ToggleButton key={value} value={value} sx={{ px: 1.5 }}>
                            {label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {columns.length === 0 && (
                <Paper sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        در این روز آموزگاری حضور ندارد
                    </Typography>
                </Paper>
            )}

            {/* p:0 is required: the theme pads all Papers 1rem, and padding sits
                outside the sticky scrollport, letting content show above the
                pinned header inside the padded strip */}
            {columns.length > 0 && (
            <Box sx={{ position: 'relative' }}>
            <Paper
                ref={scrollRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                sx={{
                    maxHeight: '72vh',
                    overflow: 'auto',
                    p: 0,
                    cursor: grabbing ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    // native scrollbars are hidden: drag-to-pan captures the
                    // pointer and breaks thumb-dragging anyway; wheel, drag,
                    // touch and the edge arrows remain
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' }
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: `52px repeat(${columns.length}, minmax(150px, 1fr))`,
                        gridTemplateRows: `40px repeat(${totalSlots}, ${SLOT_HEIGHT}px)`,
                        minWidth: columns.length * 150 + 52
                    }}
                >
                    {/* Time-axis gutter backdrop: hides columns scrolling beneath the labels.
                        background.default matches the other tables' sticky surfaces. */}
                    <Box
                        sx={{
                            gridColumn: 1,
                            gridRow: `2 / ${totalSlots + 2}`,
                            position: 'sticky',
                            insetInlineStart: 0,
                            zIndex: 3,
                            bgcolor: 'background.default'
                        }}
                    />
                    {/* Corner cell: pinned on both axes, covers header/time-axis crossing */}
                    <Box
                        sx={{
                            gridColumn: 1,
                            gridRow: 1,
                            position: 'sticky',
                            top: 0,
                            insetInlineStart: 0,
                            zIndex: 6,
                            bgcolor: 'background.default',
                            borderBottom: '1px solid',
                            borderColor: 'border.main'
                        }}
                    />
                    {/* Hour lines + time labels */}
                    {hourMarks.map((minutes) => (
                        <React.Fragment key={`hour-${minutes}`}>
                            <Typography
                                sx={{
                                    gridColumn: 1,
                                    gridRow: rowOf(minutes),
                                    position: 'sticky',
                                    insetInlineStart: 0,
                                    zIndex: 4,
                                    bgcolor: 'background.default',
                                    fontSize: '0.7rem',
                                    color: 'text.secondary',
                                    px: 0.5,
                                    mt: '-0.55em',
                                    alignSelf: 'start'
                                }}
                            >
                                {formatTime(minutes)}
                            </Typography>
                            {minutes < gridEnd && (
                                <Box
                                    sx={{
                                        gridColumn: `2 / ${columns.length + 2}`,
                                        gridRow: rowOf(minutes),
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        pointerEvents: 'none'
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))}

                    {columns.map((column, columnIndex) => {
                        const gridColumn = columnIndex + 2;

                        return (
                            <React.Fragment key={column.teacher.id}>
                                {/* Teacher header: pinned while scrolling vertically.
                                    Styled like the app's table heads: background.default
                                    surface + border.main hairline. */}
                                <Box
                                    sx={{
                                        gridColumn,
                                        gridRow: 1,
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 5,
                                        bgcolor: 'background.default',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderBottom: '1px solid',
                                        borderColor: 'border.main',
                                        px: 0.5,
                                        gap: 0.5
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                        {column.teacher.full_name}
                                    </Typography>
                                    {onEditTeacher && (
                                        <Tooltip title="ویرایش برنامه کاری">
                                            <IconButton
                                                size="small"
                                                sx={{ p: 0.25 }}
                                                onClick={() => onEditTeacher(column.teacher)}
                                            >
                                                <EditIcon sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>

                                {column.isWorking ? (
                                    /* Working-hours band: tinted area = bookable; empty tint = free slot */
                                    <Box
                                        sx={(theme) => ({
                                            gridColumn,
                                            gridRow: `${rowOf(column.workStart)} / ${rowOf(column.workEnd)}`,
                                            bgcolor: alpha(theme.palette.success.main, 0.07),
                                            borderRadius: 1,
                                            mx: 0.5,
                                            zIndex: 1
                                        })}
                                    />
                                ) : (
                                    <Typography
                                        sx={{
                                            gridColumn,
                                            gridRow: 2,
                                            textAlign: 'center',
                                            fontSize: '0.75rem',
                                            fontStyle: 'italic',
                                            color: 'text.disabled',
                                            pt: 1
                                        }}
                                    >
                                        روز غیرکاری
                                    </Typography>
                                )}

                                {column.sessions.map((slot, index) => renderSessionBlock(column, gridColumn, slot, index))}
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Paper>

            {/* Edge paging arrows: float over the grid, shown only on overflow */}
            {canScrollX && (
                <>
                    {/* Physical left/right get mirrored by the RTL stylis plugin;
                        logical inset props place these reliably: inline-start is
                        the visual right in RTL (offset past the 52px time axis). */}
                    <Tooltip title="آموزگاران قبلی">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => pageScroll(1)}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                insetInlineStart: 64,
                                transform: 'translateY(-50%)',
                                zIndex: 7,
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'border.main',
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'background.default' }
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="آموزگاران بعدی">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => pageScroll(-1)}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                insetInlineEnd: 8,
                                transform: 'translateY(-50%)',
                                zIndex: 7,
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'border.main',
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'background.default' }
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}
            </Box>
            )}
        </Box>
    );
}
